import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { Badge } from '@/design-system/primitives'
import { cn } from '@/lib/cn'
import { useApiErrorText } from '@/lib/staffGate'

import { useSegmentChange, type SegmentChange } from '../api'
import {
  QUIZ_LETTERS,
  type ProgramSegment,
  type QuizQuestion,
  type QuizQuestionFields,
} from '../types'

const INPUT =
  'mt-1 h-11 w-full min-w-0 rounded-md border border-line bg-bg-base px-3 text-content-primary'
const AREA = 'mt-1 w-full min-w-0 rounded-md border border-line bg-bg-base px-3 py-2 text-content-primary'
const LABEL = 'block min-w-0 text-caption text-content-secondary'
// Les mêmes bornes que le serveur : une réponse tient dans une case de la
// grille 2 × 2 lue à dix mètres.
const MAX_QUESTION = 300
const MAX_ANSWER = 160
const MAX_NOTE = 500

/** Les quatre réponses, lettre par lettre — la bonne marquée pour le MC seul. */
function AnswerList({ question, large = false }: { question: QuizQuestion; large?: boolean }) {
  const { t } = useTranslation()
  return (
    <ol className="mt-2 grid grid-cols-1 gap-1.5 min-[480px]:grid-cols-2">
      {question.answers.map((answer, index) => {
        const correct = question.correct_index === index
        return (
          <li
            key={QUIZ_LETTERS[index]}
            className={cn(
              'flex min-w-0 items-start gap-2 rounded-md border px-2.5 py-1.5',
              correct ? 'border-state-success/50 bg-state-success/10' : 'border-line-subtle',
              large ? 'text-base' : 'text-sm',
            )}
          >
            <span className="shrink-0 font-mono font-semibold text-champagne-300">
              {QUIZ_LETTERS[index]}
            </span>
            <span className="min-w-0 flex-1 break-words text-content-primary">{answer}</span>
            {correct ? (
              <span className="shrink-0 text-state-success" aria-label={t('conductor.quiz.correctMark')}>
                ✓
              </span>
            ) : null}
          </li>
        )
      })}
    </ol>
  )
}

// ── Préparer : les questions, dans l'onglet Programme ──────────────

/**
 * Les questions d'un quiz, à préparer avant la soirée — autant que voulu.
 *
 * Chaque question a ses quatre réponses A à D et, si le MC le souhaite, la
 * bonne : elle reste dans le conducteur, l'écran ne la reçoit qu'au moment
 * de « Révéler la réponse ».
 */
export function QuizEditor({
  eventId,
  segment,
  can,
}: {
  eventId: string | undefined
  segment: ProgramSegment
  can: (permission: string) => boolean
}) {
  const { t } = useTranslation()
  const errorText = useApiErrorText()
  const change = useSegmentChange(eventId)
  const questions = segment.questions ?? []
  const [open, setOpen] = useState<
    { kind: 'edit'; id: string } | { kind: 'delete'; id: string } | { kind: 'create' } | null
  >(null)
  const canCreate = can('program.item.create')
  const canUpdate = can('program.item.update')
  const canDelete = can('program.item.delete')
  const canReorder = can('program.item.reorder')

  const run = (payload: SegmentChange) => {
    change.mutate(payload, { onSuccess: () => setOpen(null) })
  }

  return (
    <div className="mt-3 space-y-3">
      <p className="text-caption text-content-muted">
        {questions.length > 0
          ? t('conductor.quiz.count', { count: questions.length })
          : t('conductor.quiz.empty')}
      </p>

      {change.isError ? (
        <p role="alert" className="text-sm text-state-danger">
          {errorText(change.error)}
        </p>
      ) : null}

      <ol className="space-y-2">
        {questions.map((question, index) => (
          <li key={question.id} className="rounded-md bg-bg-inset p-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="overline text-content-muted">
                {t('conductor.quiz.number', { number: index + 1 })}
              </span>
              {question.on_screen ? <Badge tone="success">{t('conductor.quiz.live')}</Badge> : null}
            </div>

            {open?.kind === 'edit' && open.id === question.id ? (
              <QuizForm
                initial={question}
                pending={change.isPending}
                onCancel={() => setOpen(null)}
                onSubmit={(fields) => run({ kind: 'questionUpdate', id: question.id, fields })}
              />
            ) : (
              <>
                <p className="mt-1 break-words font-medium text-content-primary">
                  {question.question}
                </p>
                <AnswerList question={question} />
                {question.correct_index === null ? (
                  <p className="mt-1 text-caption text-content-muted">
                    {t('conductor.quiz.noCorrect')}
                  </p>
                ) : null}
                {question.note ? (
                  <p className="mt-1 whitespace-pre-line break-words text-caption text-content-secondary">
                    {question.note}
                  </p>
                ) : null}
              </>
            )}

            {open?.kind === 'delete' && open.id === question.id ? (
              <div className="mt-3 rounded-md border border-state-danger/40 p-3">
                <p className="break-words text-sm text-content-primary">
                  {t('conductor.quiz.deleteConfirm', { number: index + 1 })}
                </p>
                {question.on_screen ? (
                  <p className="mt-1 text-caption text-state-warning">
                    {t('conductor.quiz.deleteLive')}
                  </p>
                ) : null}
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    disabled={change.isPending}
                    onClick={() => run({ kind: 'questionDelete', id: question.id })}
                  >
                    {t('conductor.plan.deleteYes')}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setOpen(null)}>
                    {t('conductor.plan.cancel')}
                  </Button>
                </div>
              </div>
            ) : open?.kind === 'edit' && open.id === question.id ? null : (
              <div className="mt-3 flex flex-wrap gap-2">
                {canReorder ? (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      aria-label={t('conductor.plan.moveUp')}
                      title={t('conductor.plan.moveUp')}
                      disabled={change.isPending || index === 0}
                      onClick={() => run({ kind: 'questionMove', id: question.id, direction: 'up' })}
                    >
                      ↑
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      aria-label={t('conductor.plan.moveDown')}
                      title={t('conductor.plan.moveDown')}
                      disabled={change.isPending || index === questions.length - 1}
                      onClick={() =>
                        run({ kind: 'questionMove', id: question.id, direction: 'down' })
                      }
                    >
                      ↓
                    </Button>
                  </>
                ) : null}
                {canUpdate ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setOpen({ kind: 'edit', id: question.id })}
                  >
                    {t('conductor.plan.edit')}
                  </Button>
                ) : null}
                {canDelete ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-state-danger"
                    onClick={() => setOpen({ kind: 'delete', id: question.id })}
                  >
                    {t('conductor.plan.delete')}
                  </Button>
                ) : null}
              </div>
            )}
          </li>
        ))}
      </ol>

      {canCreate ? (
        open?.kind === 'create' ? (
          <div className="rounded-md border border-champagne-400/40 p-3">
            <p className="overline text-content-muted">
              {t('conductor.quiz.number', { number: questions.length + 1 })}
            </p>
            <QuizForm
              initial={null}
              pending={change.isPending}
              onCancel={() => setOpen(null)}
              onSubmit={(fields) =>
                run({
                  kind: 'questionCreate',
                  segmentId: segment.id,
                  fields: fields as QuizQuestionFields,
                })
              }
            />
          </div>
        ) : (
          <Button
            size="sm"
            variant="outline"
            className="w-full sm:w-auto"
            onClick={() => setOpen({ kind: 'create' })}
          >
            {t('conductor.quiz.add')}
          </Button>
        )
      ) : null}
    </div>
  )
}

/**
 * Écrire ou corriger une question : le texte, les quatre réponses A à D, la
 * bonne réponse (facultative) et une note pour le MC.
 *
 * En modification, seuls les champs changés partent au serveur.
 */
function QuizForm({
  initial,
  pending,
  onSubmit,
  onCancel,
}: {
  initial: QuizQuestion | null
  pending: boolean
  onSubmit: (fields: Partial<QuizQuestionFields>) => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const [question, setQuestion] = useState(initial?.question ?? '')
  const [answers, setAnswers] = useState<string[]>(() =>
    QUIZ_LETTERS.map((_, index) => initial?.answers[index] ?? ''),
  )
  const [correct, setCorrect] = useState<number | null>(initial?.correct_index ?? null)
  const [note, setNote] = useState(initial?.note ?? '')
  const [error, setError] = useState<string | null>(null)

  const setAnswer = (index: number, value: string) =>
    setAnswers((current) => current.map((entry, position) => (position === index ? value : entry)))

  const submit = (formEvent: FormEvent) => {
    formEvent.preventDefault()
    setError(null)
    const cleaned = answers.map((entry) => entry.trim())
    if (!question.trim()) {
      setError(t('conductor.quiz.error.question'))
      return
    }
    if (cleaned.some((entry) => !entry)) {
      setError(t('conductor.quiz.error.answers'))
      return
    }
    const fields: QuizQuestionFields = {
      question: question.trim(),
      answers: cleaned,
      correct_index: correct,
      note: note.trim() || null,
    }
    if (!initial) {
      onSubmit(fields)
      return
    }
    const changed: Partial<QuizQuestionFields> = {}
    if (fields.question !== initial.question) changed.question = fields.question
    if (fields.answers.some((entry, index) => entry !== initial.answers[index])) {
      changed.answers = fields.answers
    }
    if (fields.correct_index !== initial.correct_index) changed.correct_index = fields.correct_index
    if (fields.note !== (initial.note ?? null)) changed.note = fields.note
    if (Object.keys(changed).length === 0) {
      onCancel()
      return
    }
    onSubmit(changed)
  }

  return (
    <form className="mt-3 space-y-3" onSubmit={submit}>
      <label className={LABEL}>
        {t('conductor.quiz.field.question')}
        <textarea
          rows={2}
          value={question}
          maxLength={MAX_QUESTION}
          autoFocus={!initial}
          onChange={(change) => setQuestion(change.target.value)}
          className={AREA}
        />
      </label>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {QUIZ_LETTERS.map((letter, index) => (
          <label key={letter} className={LABEL}>
            {t('conductor.quiz.field.answer', { letter })}
            <input
              value={answers[index]}
              maxLength={MAX_ANSWER}
              onChange={(change) => setAnswer(index, change.target.value)}
              className={INPUT}
            />
          </label>
        ))}
      </div>

      <fieldset className="min-w-0">
        <legend className="text-caption text-content-secondary">
          {t('conductor.quiz.field.correct')}
        </legend>
        {/* « Aucune » prend sa largeur ; les quatre lettres se partagent le reste. */}
        <div className="mt-1 grid grid-cols-[auto_repeat(4,minmax(0,1fr))] gap-2">
          {[null, 0, 1, 2, 3].map((value) => {
            const checked = correct === value
            return (
              <label
                key={value ?? 'none'}
                className={cn(
                  'flex min-h-11 min-w-0 cursor-pointer items-center justify-center rounded-md border px-3 text-sm transition',
                  checked
                    ? 'border-champagne-400/60 bg-champagne-400/10 text-champagne-300'
                    : 'border-line-subtle text-content-secondary hover:text-content-primary',
                )}
              >
                <input
                  type="radio"
                  name={`correct-${initial?.id ?? 'new'}`}
                  className="sr-only"
                  checked={checked}
                  onChange={() => setCorrect(value)}
                />
                <span className="truncate">
                  {value === null ? t('conductor.quiz.field.none') : QUIZ_LETTERS[value]}
                </span>
              </label>
            )
          })}
        </div>
      </fieldset>

      <label className={LABEL}>
        {t('conductor.quiz.field.note')}
        <input
          value={note}
          maxLength={MAX_NOTE}
          placeholder={t('conductor.quiz.placeholder.note')}
          onChange={(change) => setNote(change.target.value)}
          className={INPUT}
        />
      </label>

      {error ? (
        <p role="alert" className="text-sm text-state-danger">
          {error}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button type="submit" size="sm" disabled={pending}>
          {t('conductor.plan.save')}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          {t('conductor.plan.cancel')}
        </Button>
      </div>
    </form>
  )
}

// ── Dérouler : poser les questions à l'écran, dans l'onglet Direct ─

/**
 * Le quiz pendant la soirée : chaque question, ses gestes d'écran, et le
 * raccourci « Question suivante » sous le pouce.
 *
 * La bonne réponse est montrée au MC (coche verte) — jamais à l'écran avant
 * « Révéler la réponse ».
 */
export function LiveQuiz({
  segment,
  canScreen,
  pending,
  onChange,
}: {
  segment: ProgramSegment
  canScreen: boolean
  pending: boolean
  onChange: (payload: SegmentChange) => void
}) {
  const { t } = useTranslation()
  const questions = segment.questions ?? []
  const liveIndex = questions.findIndex((question) => question.on_screen)
  const live = liveIndex >= 0 ? questions[liveIndex] : null
  // Sans question à l'écran, le raccourci pose la première ; après la
  // dernière, il n'y a plus rien à enchaîner.
  const next = questions[liveIndex + 1] ?? null

  if (questions.length === 0) {
    return <p className="mt-2 text-sm text-content-secondary">{t('conductor.quiz.empty')}</p>
  }

  return (
    <div className="mt-3 space-y-3">
      {canScreen ? (
        next ? (
          <Button
            variant="ember"
            className="w-full min-w-0 whitespace-normal sm:w-auto"
            disabled={pending}
            onClick={() => onChange({ kind: 'questionShow', id: next.id })}
          >
            {live
              ? t('conductor.quiz.next', { number: liveIndex + 2, total: questions.length })
              : t('conductor.quiz.start')}
          </Button>
        ) : (
          <p className="text-caption text-content-muted">{t('conductor.quiz.lastAsked')}</p>
        )
      ) : null}

      <ol className="space-y-2">
        {questions.map((question, index) => (
          <li
            key={question.id}
            className={cn(
              'rounded-md border p-3',
              question.on_screen ? 'border-2 border-state-success/70 bg-state-success/5' : 'border-line-subtle',
            )}
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="overline text-content-muted">
                {t('conductor.quiz.numberOf', { number: index + 1, total: questions.length })}
              </span>
              {question.on_screen ? (
                <Badge tone="success">
                  {question.revealed ? t('conductor.quiz.revealed') : t('conductor.quiz.live')}
                </Badge>
              ) : null}
            </div>
            <p className="mt-1 break-words font-display text-heading-sm text-content-primary">
              {question.question}
            </p>
            <AnswerList question={question} large />
            {question.note ? (
              <p className="mt-2 whitespace-pre-line break-words text-sm text-content-secondary">
                {question.note}
              </p>
            ) : null}

            {canScreen ? (
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant={question.on_screen ? 'outline' : 'champagne'}
                  className="min-w-0 whitespace-normal text-left"
                  disabled={pending}
                  onClick={() => onChange({ kind: 'questionShow', id: question.id })}
                >
                  {question.on_screen ? t('conductor.quiz.showAgain') : t('conductor.quiz.show')}
                </Button>
                {question.on_screen && !question.revealed && question.correct_index !== null ? (
                  <Button
                    size="sm"
                    variant="ember"
                    disabled={pending}
                    onClick={() => onChange({ kind: 'questionReveal', id: question.id })}
                  >
                    {t('conductor.quiz.reveal')}
                  </Button>
                ) : null}
                {question.on_screen ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={pending}
                    onClick={() => onChange({ kind: 'clear' })}
                  >
                    {t('conductor.segments.removeFromScreen')}
                  </Button>
                ) : null}
              </div>
            ) : null}
            {question.on_screen && question.correct_index === null ? (
              <p className="mt-2 text-caption text-content-muted">
                {t('conductor.quiz.noCorrectHint')}
              </p>
            ) : null}
          </li>
        ))}
      </ol>
    </div>
  )
}
